import { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { SEARCH_DEBOUNCE_MS } from '../helpers/constants';
import { applyFilters } from '../helpers/catalogFilterUtils';

/**
 * Custom hook for debounced value
 * @param {any} value - Value to debounce
 * @param {number} delay - Debounce delay in ms
 * @returns {any} Debounced value
 */
function useDebouncedValue(value, delay = SEARCH_DEBOUNCE_MS) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook for filtering catalog items with search and tags
 * @param {Array} initialState - Initial items array
 * @param {Function} compareFunc - Function to compare item with search term
 * @param {boolean} stopRender - Flag to prevent initial render updates
 * @returns {Object} Filter state and handlers
 */
export default function useFilter(initialState, compareFunc, stopRender) {
  const [filterValue, setFilterValue] = useState('');
  const { selectedTags } = useSelector(state => state.assaysCatalog);
  const prevTagsLengthRef = useRef(selectedTags?.length || 0);

  // Debounce the filter value for performance
  const debouncedFilterValue = useDebouncedValue(filterValue);

  // Memoized filtered state - recalculates only when dependencies change
  const filteredState = useMemo(() => {
    if (!initialState) return [];
    if (stopRender) return [];

    return applyFilters(initialState, debouncedFilterValue, selectedTags, compareFunc);
  }, [initialState, debouncedFilterValue, selectedTags, compareFunc, stopRender]);

  // Track tag changes for external consumers
  useEffect(() => {
    prevTagsLengthRef.current = selectedTags?.length || 0;
  }, [selectedTags]);

  const handleChangeFilterValue = (event) => {
    const newValue = event.target.value;
    setFilterValue(newValue);
  };

  const backToInitialState = () => {
    setFilterValue('');
  };

  return {
    filterValue,
    handleChangeFilterValue,
    backToInitialState,
    filteredState,
  };
}
